/* eslint-disable max-len */
import React, {
  useState,
  useEffect,
  useCallback,
} from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';

import useComponentVisible from './useComponentVisible';

import {
  Button,
  ButtonTester,
  ContainerAction,
  ContainerButton,
  ContainerButtonTester,
  ContainerClickable,
  ContainerClickIcon,
  ContainerIcon,
  ContainerInput,
  ContainerLabel,
  Icon,
  Input,
  RowContainer,
  ShadowRow,
} from './styles';

const DEFAULT_TEXT = 'Label node - double click to edit';

const KEYS_PRESS = {
  SHIFT: 'Shift',
  ENTER: 'Enter',
  TAB: 'Tab',
};

const MOVE_DIRECTION = {
  UP: 'up',
  DOWN: 'down',
  INTO: 'into',
};

/**
 * This component is used to show the tree nodes (rows).
 * The component can show the action buttons if the user double click or hover the mouse on it.
 * The user can click above the Icon Arrow to expand or collapse all the node children.
 * Users can edit the node label by double click on node.
*/
const Row = (props) => {
  const {
    changeBackgroundClickRow,
    childData,
    currentIdChildDrag,
    currentNodeIdEditMode,
    currentTesterButton,
    handleChangeBackgroundClickRow,
    handleCurrentRowSelectedAccordion,
    handleRowClick,
    handleShowCurrentTesterButton,
    hasChild,
    isExpanded,
    isLastChild,
    isRoot,
    isTester, // Used to know if the component is displayed in the tester platform.
    lastChildAddedToEditMode,
    level,
    marginLeft,
    onAddChild,
    onChangeToEditModeLastChildAdded,
    onClickNode,
    onCollapseAutoExpandedRow,
    onCurrentIdChildDrag,
    onCurrentNodeIdEditMode,
    onDragAndDrop,
    onEditChild,
    onElementPermutation,
    onRemoveChild,
    onRowDragHover,
    onTesterClick,
    rowDragHoverId,
    loading,
  } = props;

  const {
    id,
    label,
  } = childData;

  const isCurrentNodeInEditMode = currentNodeIdEditMode === id;
  const isHoveringByDragdRow = rowDragHoverId === id;

  const [showEditContent, setShowEditContent] = useState(isCurrentNodeInEditMode);
  const [showAction, setShowAction] = useState(false);
  const [showTesterButton, setShowTesterButton] = useState(false);

  const { ref, isComponentVisible, setIsComponentVisible } = useComponentVisible(true);

  const handleChangeToEditMode = useCallback(() => {
    if (!isTester) {
      setShowEditContent(true);
      setIsComponentVisible(true);
      onCurrentNodeIdEditMode(childData.id);
    }
  }, [childData.id, isTester, onCurrentNodeIdEditMode, setIsComponentVisible]);

  const handleSelectTextOnFocus = ({ target }) => {
    if (!isTester) {
      target.select();
    }
  };

  const handleEdit = useCallback(({ target }) => {
    if (!isTester) {
      const editedData = { ...childData, label: target.value };
      onEditChild(editedData);
    }
  }, [childData, isTester, onEditChild]);

  const closeEditMode = useCallback(() => {
    if (!isTester) {
      setShowEditContent(false);
      onCurrentNodeIdEditMode(null);
    }
  }, [isTester, onCurrentNodeIdEditMode]);

  const handleAddChild = useCallback(() => {
    if (!isTester) {
      closeEditMode();

      if (!isExpanded) {
        handleRowClick();
      }

      const newChild = { ...childData, label: DEFAULT_TEXT };

      onAddChild(id, newChild);
    }
  }, [childData, closeEditMode, handleRowClick, id, isExpanded, isTester, onAddChild]);

  const handleAddSibling = useCallback(() => {
    if (!isTester) {
      closeEditMode();

      if (!isExpanded) {
        handleRowClick();
      }

      const newChild = { ...childData, label: DEFAULT_TEXT };

      onAddChild(newChild.parentId, newChild);
    }
  }, [childData, closeEditMode, handleRowClick, isExpanded, isTester, onAddChild]);

  const handleRemoveChild = useCallback(() => {
    if (!isTester) {
      onRemoveChild(id);
      closeEditMode();
    }
  }, [closeEditMode, id, isTester, onRemoveChild]);

  /**
   * Used when the component is used in the tester platform to show a button at the end of the row.
   */
  const handleShowTesterButton = useCallback(() => {
    if (isTester) {
      if (!hasChild) {
        handleShowCurrentTesterButton(id);
      } else {
        handleShowCurrentTesterButton('');
      }
    }
  }, [handleShowCurrentTesterButton, hasChild, id, isTester]);

  /**
   * Used when the component is used in the tester platform to expand only one row and collapse
   * the other rows.
   */
  const handleExpandRowAccordion = useCallback(() => {
    if (isTester) {
      if (hasChild) {
        handleCurrentRowSelectedAccordion(handleRowClick, id, level);
      } else {
        handleShowTesterButton();
        handleChangeBackgroundClickRow(id);
      }
      // Get the id of the current clicked row.
      onClickNode({ id, level });
    }
  // eslint-disable-next-line max-len
  }, [handleChangeBackgroundClickRow, handleCurrentRowSelectedAccordion, handleRowClick, handleShowTesterButton, hasChild, id, isTester, level, onClickNode]);

  const onRowHover = useCallback(() => {
    if (!isTester) {
      setShowAction(true);
    } else {
      handleShowTesterButton();
    }
  }, [handleShowTesterButton, isTester]);

  const onRowLeave = useCallback(() => { if (!isTester) { setShowAction(false); } }, [isTester]);

  const handleRowExpandCollapse = () => {
    if (!isTester) {
      handleRowClick();
      onCollapseAutoExpandedRow();
    }
  };

  /**
   * Used to get the id of the row where the button appear and to execute
   * the action of the tester button.
   */
  const handleTesterClick = async () => {
    if (isTester) {
      // Save the paths in the database and go to the next task or question.
      await onTesterClick({ id, level });
    }
  };

  /**
   * When the selected row by the user is being drag this function is called.
   */
  const handleDragStart = () => {
    if (!isTester && !showEditContent && !isRoot) {
      // Collapse the current row if its is expanded
      if (isExpanded) {
        handleRowClick();
      }

      // Get the id of the current row that is being drag.
      onCurrentIdChildDrag(id);
    }
  };

  /**
   * Whene the user finish dragging a row this function is called.
   */
  const handleDragEnd = () => {
    if (!isTester && !showEditContent && !isRoot) {
      onCurrentIdChildDrag(null);
      onRowDragHover(null);
    }
  };

  /**
   * When the current row that is being drag hover another row, this function is called.
   * @param {*} event
   */
  const handleDragOver = (event) => {
    if (!isTester && !isRoot && currentIdChildDrag !== id) {
      event.preventDefault();
      onRowDragHover(id);
    }
  };

  /**
   * When the user release the row that was being drag into the area of drop, this function is called.
   * @param {*} event
   * @param {*} moveDirection // The diretion to insert the row (up, down, into)
   */
  const handleDrop = (event, moveDirection) => {
    if (!isTester && !showEditContent && !isRoot) {
      event.preventDefault();

      onDragAndDrop(currentIdChildDrag, id, moveDirection);

      onCurrentIdChildDrag(null);
      onRowDragHover(null);
    }
  };

  /**
   * Used to close the edit mode.
   */
  useEffect(() => {
    if (!isTester && !isComponentVisible && showEditContent) {
      closeEditMode();
    }
  }, [closeEditMode, isComponentVisible, isTester, showEditContent]);

  /**
   * Used to automatically expand the root when the user click on it.
   */
  useEffect(() => {
    if (isTester && isRoot && !isExpanded) {
      handleRowClick();
    }
  }, [handleRowClick, isExpanded, isRoot, isTester]);

  /**
   * Used to only show one "I'd find it here" button by child without children.
   */
  useEffect(() => {
    if (isTester && !hasChild && currentTesterButton === id) {
      setShowTesterButton(true);
    } else {
      setShowTesterButton(false);
    }
  }, [currentTesterButton, hasChild, id, isTester]);

  /**
   * Change to edit mode the last child added.
   */
  useEffect(() => {
    if (!isTester && lastChildAddedToEditMode === id) {
      onChangeToEditModeLastChildAdded(null);
      handleChangeToEditMode();
    }
  }, [handleChangeToEditMode, id, isTester, lastChildAddedToEditMode, onChangeToEditModeLastChildAdded]);

  /**
  * Used to verify if the current row label is empty and to set the default value.
  */
  useEffect(() => {
    if (!isTester && !showEditContent && _.isEmpty(label.trim())) {
      const editedData = { ...childData, label: DEFAULT_TEXT };
      onEditChild(editedData);
    }
  }, [childData, isTester, label, onEditChild, showEditContent]);

  // Used to execute actions when using shortcuts.
  const handleKeyUp = (event) => {
    if (!isTester) {
      if (event.key === KEYS_PRESS.ENTER && event.shiftKey) {
        // Action to Add a new child node. (SHIFT + ENTER)
        handleAddChild();
      } else if (event.key === KEYS_PRESS.ENTER) {
        // Action to Add a new sibling node. (ENTER)
        handleAddSibling();
      } else if (event.key === KEYS_PRESS.TAB && event.shiftKey) {
        // Action to move a node up. (SHIFT + TAB)
        onElementPermutation(currentNodeIdEditMode, MOVE_DIRECTION.UP);
      } else if (event.key === KEYS_PRESS.TAB) {
        // Action to move a node down. (TAB)
        onElementPermutation(currentNodeIdEditMode, MOVE_DIRECTION.DOWN);
      }
    }
  };

  const handleKeyDown = (event) => {
    if (!isTester) {
      if (event.key === KEYS_PRESS.SHIFT || event.key === KEYS_PRESS.TAB) {
        event.preventDefault();
      }
    }
  };

  return (
    <>
      {isHoveringByDragdRow && !isRoot && !isTester && !showEditContent && (
        <ShadowRow
          marginLeft={marginLeft}
          showShadowRow={isHoveringByDragdRow}
          rowDragHoverId={rowDragHoverId}
          onDrop={(e) => { handleDrop(e, MOVE_DIRECTION.UP); }}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
        />
      )}

      <RowContainer
        marginLeft={marginLeft}
        showEditContent={showEditContent && !isRoot}
        isTester={isTester}
        hasChild={hasChild}
        onClick={handleExpandRowAccordion}
        changeBackgroundClickRow={changeBackgroundClickRow === id}
        draggable={!isTester && !showEditContent && !isRoot}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragOver={handleDragOver}
        onDrop={(e) => { handleDrop(e, MOVE_DIRECTION.INTO); }}
      >

        {(isRoot || (!isRoot && !showEditContent)) && hasChild && (
          <ContainerClickIcon
            onClick={handleRowExpandCollapse}
            onMouseEnter={handleShowTesterButton}
          >
            <ContainerIcon>
              {hasChild && (
                <Icon name={isExpanded ? 'caret down' : 'caret right'} />
              )}
            </ContainerIcon>
          </ContainerClickIcon>
        )}

        <ContainerAction
          ref={ref}
          showEditContent={showEditContent && !isRoot}
          onMouseEnter={onRowHover}
          onMouseLeave={onRowLeave}
        >
          <ContainerClickable
            onDoubleClick={handleChangeToEditMode}
          >
            {(isRoot || (!isRoot && !showEditContent)) && (
              <ContainerLabel hasChild={hasChild}>
                {label}
              </ContainerLabel>
            )}

            {!isRoot && showEditContent && !isTester && (
              <ContainerInput>
                <Input
                  autoFocus
                  onFocus={handleSelectTextOnFocus}
                  fluid
                  placeholder={label}
                  value={label}
                  onChange={handleEdit}
                  onKeyDown={handleKeyDown}
                  onKeyUp={handleKeyUp}
                />
              </ContainerInput>
            )}
          </ContainerClickable>

          <ContainerButton>
            {(showEditContent || showAction) && !isTester && (
              <>
                <Button onClick={handleAddChild}>
                  Add Child
                </Button>
                {!isRoot && (
                  <Button onClick={handleRemoveChild}>
                    Remove
                  </Button>
                )}
              </>
            )}

          </ContainerButton>
        </ContainerAction>

        {isTester && showTesterButton && (
          <ContainerButtonTester>
            <ButtonTester
              loading={loading}
              disabled={loading}
              onClick={() => !loading && handleTesterClick()}
            >
              {'I\'d find it here'}
            </ButtonTester>
          </ContainerButtonTester>
        )}

      </RowContainer>

      {isHoveringByDragdRow && !isRoot && !isTester && !showEditContent && isLastChild && (!isExpanded || !hasChild) && (
        <ShadowRow
          marginLeft={marginLeft}
          showShadowRow={isHoveringByDragdRow}
          onDrop={(e) => { handleDrop(e, MOVE_DIRECTION.DOWN); }}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
        />
      )}

    </>
  );
};

Row.propTypes = {
  childData: PropTypes.shape({
    label: PropTypes.string,
    id: PropTypes.string.isRequired,
    order: PropTypes.number,
    parentId: PropTypes.string,
  }).isRequired,
  changeBackgroundClickRow: PropTypes.string,
  currentIdChildDrag: PropTypes.string,
  currentNodeIdEditMode: PropTypes.string,
  currentTesterButton: PropTypes.string,
  handleChangeBackgroundClickRow: PropTypes.func,
  handleCurrentRowSelectedAccordion: PropTypes.func,
  handleRowClick: PropTypes.func,
  handleShowCurrentTesterButton: PropTypes.func,
  hasChild: PropTypes.bool,
  isExpanded: PropTypes.bool,
  isLastChild: PropTypes.bool,
  isRoot: PropTypes.bool,
  isTester: PropTypes.bool,
  lastChildAddedToEditMode: PropTypes.string,
  level: PropTypes.number,
  marginLeft: PropTypes.number,
  onAddChild: PropTypes.func,
  onChangeToEditModeLastChildAdded: PropTypes.func,
  onClickNode: PropTypes.func,
  onCollapseAutoExpandedRow: PropTypes.func,
  onCurrentIdChildDrag: PropTypes.func,
  onCurrentNodeIdEditMode: PropTypes.func,
  onDragAndDrop: PropTypes.func,
  onEditChild: PropTypes.func,
  onElementPermutation: PropTypes.func,
  onRemoveChild: PropTypes.func,
  onRowDragHover: PropTypes.func,
  onTesterClick: PropTypes.func,
  rowDragHoverId: PropTypes.string,
  loading: PropTypes.bool,
};

Row.defaultProps = {
  changeBackgroundClickRow: '',
  currentIdChildDrag: null,
  currentNodeIdEditMode: null,
  currentTesterButton: '',
  handleChangeBackgroundClickRow: () => {},
  handleCurrentRowSelectedAccordion: () => {},
  handleRowClick: () => {},
  handleShowCurrentTesterButton: () => {},
  hasChild: true,
  isExpanded: false,
  isLastChild: false,
  isRoot: false,
  isTester: false,
  lastChildAddedToEditMode: null,
  level: 1,
  marginLeft: null,
  onAddChild: () => {},
  onChangeToEditModeLastChildAdded: () => {},
  onClickNode: () => {},
  onCollapseAutoExpandedRow: () => {},
  onCurrentIdChildDrag: () => {},
  onCurrentNodeIdEditMode: () => {},
  onDragAndDrop: () => {},
  onEditChild: () => {},
  onElementPermutation: () => {},
  onRemoveChild: () => {},
  onRowDragHover: () => {},
  onTesterClick: () => {},
  rowDragHoverId: null,
  loading: false,
};

export default Row;
